import { brand, needs, routes } from './site';
import { blogPage } from './pages/blog';
import { legalDocs, legalHub } from './pages/yasal';

/*
 * Sayfa başına arama motoru ve paylaşım metni: sekme/arama başlığı, açıklama ve isteğe bağlı bayraklar.
 * Adresle (`routes`) eşlenir; kabuklar (`SubpageShell`, `PageShell`) adresten bulup `components/Seo` ile
 * çizer. Başlıklar sayfa hero'sunun sloganı değil, aramada okunacak anahtar kelimeli ad olarak yazılır
 * (hero "Gerçek kârını bilmeden büyüme." derken başlık "Kârlılık Analizi: …" olur); açıklamalar 150–160
 * karakterde kalır (Google sonuçta fazlasını keser). Sayfanın sloganıyla aynı olması gereken metin
 * (çözüm sayfalarının girişi) içerik dosyasından okunur ki iki yerde ayrışmasın. Sabit sayfa `sitemap.xml`
 * listesi de bu kayıttan üretilir (`scripts/sitemap.ts`); `noindex` sayfalar listeye girmez.
 */

/** Canonical ve paylaşım adreslerinin kökü; `www` canlıda buraya yönlenir. */
export const SITE_URL = 'https://zhesap.com';

export interface PageSeo {
  /** Sayfa başlığı; sonuna "| ZHesap" eklenir (`pageTitle`), marka adını zaten taşıyan başlığa eklenmez. */
  title: string;
  description: string;
  /** Dizine girmesin (404, "Çok Yakında"): `robots noindex`, canonical ve sitemap'ten düşer. */
  noindex?: boolean;
  /** Paylaşım görseli (kök göreli ya da tam adres); verilmezse varsayılan kart. */
  image?: string;
  imageSize?: { width: number; height: number };
  type?: 'website' | 'article';
  /** Blog yazısı: Open Graph `article:*` etiketleri. */
  article?: { publishedTime: string; modifiedTime?: string | null; author?: string };
  /** Yapılandırılmış veri (schema.org), gövdeye `<script type="application/ld+json">` olarak yazılır. */
  jsonLd?: Record<string, unknown>;
}

export const defaultSeo = {
  description:
    'Pazaryeri ve e-ticaret siparişlerinde gerçek net kârını gör. Komisyon, kargo, iade ve kesintiler otomatik hesaplanır; hakediş, kampanya, stok ve rakip takibi tek panelde.',
  /** 1200×630 paylaşım kartı (`public/assets/og-default.jpg`). */
  image: '/assets/og-default.jpg',
  imageSize: { width: 1200, height: 630 },
};

export const pageTitle = (title: string) => (title.includes(brand.name) ? title : `${title} | ${brand.name}`);

/** Kuruluş logosu; yapılandırılmış veride yayıncı/kuruluş olarak geçer. */
export const ORG_LOGO = `${SITE_URL}/assets/z-hesap-logo.png`;

/** Ana sayfadaki kuruluş ve site tanımı (arama sonucunda logo/künye için). */
const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: brand.name,
      url: SITE_URL,
      logo: ORG_LOGO,
      email: brand.email,
      telephone: brand.phoneHref.replace('tel:', ''),
      address: {
        '@type': 'PostalAddress',
        streetAddress: brand.addressDetail,
        addressLocality: 'Çekmeköy',
        addressRegion: 'İstanbul',
        addressCountry: 'TR',
      },
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: brand.name,
      url: SITE_URL,
      inLanguage: 'tr',
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
  ],
};

/** "İhtiyaca göre" çözüm sayfasının giriş cümlesi; açıklama olarak hero'yla aynı kalır. */
const needLead = (href: string) => needs.find((need) => need.href === href)?.lead ?? defaultSeo.description;

const pages: Record<string, PageSeo> = {
  '/': {
    title: `${brand.name} | E-Ticaret Kârlılık Analizi ve Finansal Karar Platformu`,
    description: defaultSeo.description,
    jsonLd: organizationJsonLd,
  },
  [routes.entegrasyonlar]: {
    title: 'Entegrasyonlar: Pazaryeri, E-Ticaret Altyapısı, ERP ve Kargo',
    description:
      'Trendyol, Hepsiburada, e-ticaret altyapıları, ERP ve kargo entegrasyonlarıyla maliyet, satış, kesinti ve gönderi verileri ZHesap’a otomatik akar. Desteklenen sistemleri gör.',
  },
  [routes.fiyatlar]: {
    title: 'Fiyatlandırma ve Paketler',
    description:
      'Fiyatımız net: Starter, Scale ve Signature paketleri aylık sipariş sayına göre fiyatlanır. Kargo ve pazaryeri faturaların anlaşmanla tek tek karşılaştırılır.',
  },
  [routes.iletisim]: {
    title: 'İletişim',
    description:
      'Demo talebi, iş birliği ve sorularınız için ZHesap ekibine ulaşın. E-posta, telefon ve iletişim formu; mesajlara en geç 1 iş günü içinde dönüş.',
  },
  [routes.sss]: {
    title: 'Sıkça Sorulan Sorular',
    description:
      'ZHesap hakkında merak edilenler: kurulum, entegrasyonlar, paketler, fiyatlandırma, veri güvenliği ve destek. Cevabını bulamazsan ekibimize yaz.',
  },
  [routes.blog]: {
    title: `Blog: ${blogPage.hero.title}`,
    description: blogPage.hero.lead,
  },
  [routes.kurumsal.partnerlik]: {
    title: 'Partner Programı: Ajans ve Danışmanlar İçin Süresiz Hakediş',
    description:
      'Ajanslar ve e-ticaret danışmanları için ZHesap Partner Programı: yönlendirdiğin her müşterinin her ödemesinden, müşterin kaldığı sürece partner hakedişi kazan.',
  },
  [routes.kurumsal.bizKimiz]: {
    title: 'Biz Kimiz? ZHesap’ın Hikâyesi ve Kurucuları',
    description:
      'ZHesap, e-ticaret işletmelerinin kaçan parasını bulan ve kârını koruyan finansal kontrol platformu. 15+ yıllık e-ticaret tecrübesiyle kurulan ekibi tanıyın.',
  },
  [routes.yasal.root]: {
    title: 'Yasal Merkez',
    description: legalHub.lead,
  },

  // Ürünler
  [routes.ozellikler.root]: {
    title: 'ZHesap Ürünleri: Kârlılık, Mutabakat, Kampanya ve Stok',
    description:
      'ZHesap’ın tüm modülleri: kârlılık analizi, kargo ve hakediş mutabakatı, Kampanya Sihirbazı, stok zekâsı, akıllı uyarı, rakip takibi ve AI müşteri yanıtı.',
  },
  [routes.araclar.chrome]: {
    title: 'ZHesap Chrome Eklentisi: Pazaryeri Panelinde Net Kâr',
    description:
      'ZHesap tarayıcı eklentisi Trendyol, Hepsiburada, n11 ve Boyner satıcı panellerinde siparişlere, kampanya ekranlarına ve ürün listesine net kârı ekler.',
  },
  [routes.ozellikler.karlilik]: {
    title: 'Kârlılık Analizi: Sipariş Bazında Gerçek Net Kâr',
    description:
      'Satış, maliyet, komisyon, kargo, indirim, vergi ve kesintileri birleştirir; her sipariş, ürün ve satış kanalında gerçekte ne kazandığını gösterir.',
  },
  [routes.ozellikler.hakedis]: {
    title: 'Finans & Mutabakat: Hakediş ve Kargo Fatura Kontrolü',
    description:
      'Pazaryerinden alman gereken hakedişi sipariş bazında hesaplar, ödemelerle karşılaştırır; eksik ödemeleri ve hatalı kargo kesintilerini otomatik bulur.',
  },
  [routes.ozellikler.kampanya]: {
    title: 'Kampanya Sihirbazı: Kârlı Kampanyalara Otomatik Katıl',
    description:
      'Kampanya Sihirbazı Trendyol’un 6 kampanya kurgusunu tek ekranda toplar, her teklifte net kârı önceden hesaplar; kârlı ürünleri otomatik katar, zarar ettirecekleri dışarıda bırakır.',
  },
  [routes.ozellikler.stok]: {
    title: 'Stok Zekâsı: Tükenen Ürünler ve Stokta Bekleyen Sermaye',
    description:
      'Satış hızı ve stok seviyesini birlikte analiz eder; tükenmek üzere olan ürünleri önceden gösterir, satmayan stokta bekleyen sermayeyi görünür kılar.',
  },
  [routes.ozellikler.rekabet]: {
    title: 'Rakip & Sektör Analizi: Fiyat, Stok ve BuyBox Takibi',
    description:
      'Rakiplerinin fiyat, stok, kampanya ve BuyBox hareketlerini kendi ürünlerinle yan yana izle; sektör ve kategori trendlerini takip et, fiyatı veriyle belirle.',
  },
  [routes.ozellikler.uyari]: {
    title: 'Akıllı Uyarı Sistemi: Kârı Etkileyen Gelişmeleri Anında Öğren',
    description:
      'Verilerini her gün senin yerine izler: kâr düştüğünde, bir hata oluştuğunda ya da fırsat belirdiğinde anında haber verir; panelin başında beklemen gerekmez.',
  },
  [routes.ozellikler.crm]: {
    title: 'AI Müşteri İlişkileri: Pazaryeri Sorularına Yapay Zekâ Yanıtı',
    description:
      'Pazaryerlerinden gelen müşteri sorularına mağazana uygun yanıtları saniyeler içinde üret; her yanıtı onaylayarak ya da tamamen otomatik gönder.',
  },
  [routes.ozellikler.zeki]: {
    title: 'Z’eki: Yapay Zekâ Şirket Asistanı (Beta)',
    description:
      'Z’eki, ZHesap’taki tüm verini analiz eden şirket asistanı: raporlar arasında kaybolmadan sor, neyin neden değiştiğini ve hangi aksiyonu alman gerektiğini öğren.',
  },

  // Çözümler: satış kanalına göre
  [routes.cozumler.pazaryeri]: {
    title: 'Pazaryeri Satıcıları İçin Kârlılık ve Hakediş Takibi',
    description:
      'Trendyol, Hepsiburada ve diğer pazaryerlerindeki mağazalarını tek panelde birleştir; komisyon, kargo, hizmet bedeli ve stopaj sonrası gerçek kârını gör.',
  },
  [routes.cozumler.eticaret]: {
    title: 'Kendi E-Ticaret Siteniz İçin Kârlılık Analizi',
    description:
      'E-ticaret sitenden gelen siparişleri ürün maliyeti, sanal POS komisyonu, kargo, kupon ve iadelerle hesapla; sitende gerçekte ne kazandığını sipariş bazında gör.',
  },
  [routes.cozumler.cokluMarka]: {
    title: 'Çoklu Marka ve Mağaza Yönetimi: Konsolide Kârlılık',
    description:
      'Birden fazla marka ve mağazayı tek panelde birleştir; toplam tabloyu tek bakışta gör, istediğin an marka, mağaza ve kanal bazında detaya in.',
  },
  [routes.cozumler.omnichannel]: {
    title: 'Omnichannel Satış: Mağaza, E-Ticaret ve Pazaryeri Tek Tabloda',
    description:
      'Fiziksel mağaza, e-ticaret sitesi ve pazaryeri mağazalarını tek panelde birleştiren omnichannel çözümü çok yakında. Tüm kanallarda gerçekte ne kazandığını tek yerden gör.',
  },

  // Çözümler: ihtiyaca göre (giriş cümlesi sayfayla aynı)
  [routes.cozumler.kampanya]: {
    title: 'Kampanyalarını Kârlı Yönet: Kampanya Kârlılığı Çözümü',
    description: needLead(routes.cozumler.kampanya),
  },
  [routes.cozumler.karlilik]: {
    title: 'Kârlılığını Artır: Nerede Kazanıp Nerede Kaybettiğini Gör',
    description: needLead(routes.cozumler.karlilik),
  },
  [routes.cozumler.kayip]: {
    title: 'Finansal Kayıpları Bul: Eksik Hakediş ve Hatalı Kesinti',
    description: needLead(routes.cozumler.kayip),
  },
  [routes.cozumler.stok]: {
    title: 'Stoklarını Doğru Yönet: Tükenen Ürünler ve Bekleyen Para',
    description: needLead(routes.cozumler.stok),
  },
  [routes.cozumler.rakip]: {
    title: 'Rakiplerini Takip Et: Fiyat, Stok, Kampanya ve BuyBox',
    description: needLead(routes.cozumler.rakip),
  },
};

// Yasal metinler: başlık ve özet belge tanımından (içerikleri CMS'ten gelse de adres ve başlık koddadır).
for (const doc of legalDocs) pages[doc.href] = { title: doc.title, description: doc.summary };

export const notFoundSeo: PageSeo = {
  title: 'Sayfa bulunamadı',
  description: 'Aradığınız sayfa taşınmış veya hiç var olmamış olabilir.',
  noindex: true,
};

/** Adresin SEO kaydı (canonical yolu eklenmiş); kayıt yoksa `undefined` (kayıt eksik demektir). */
export function pageSeo(href: string): (PageSeo & { path: string }) | undefined {
  const entry = pages[href];
  return entry && { ...entry, path: href };
}

/** `sitemap.xml`'e giren sabit sayfalar; blog yazıları build sırasında CMS'ten eklenir. */
export function sitemapPaths(): string[] {
  return Object.entries(pages)
    .filter(([, entry]) => !entry.noindex)
    .map(([href]) => href);
}
